<script>
import DesktopMenu from "./Desktop/DesktopMenu.vue";
import ExpandedContent from "./Desktop/ExpandedContent.vue";
import MobileMenuContent from "./Mobile/MobileMenuContent.vue";
import MobileMenuToggler from "./Mobile/MobileMenuToggler.vue";
import ProviderSubHeader from "./Mobile/ProviderSubHeader.vue";
import Logo from "./Logo.vue";
import { getHeaderData } from "./api";
import { isPatientRoute } from "../../services/route-helpers";

const MOBILE_HEADER_BREAKPOINT = 960;

export default {
  props: {
    storyblokVersion: {
      type: String,
      required: true,
    },
  },
  components: {
    DesktopMenu,
    MobileMenuContent,
    Logo,
    ExpandedContent,
    MobileMenuToggler,
    ProviderSubHeader,
  },
  data() {
    return {
      isMobile: window.innerWidth < MOBILE_HEADER_BREAKPOINT,
      headerResizeObserver: null,
      overviewPagesData: null,
      // Desktop
      desktopMenuIsOpen: false,
      // Mobile
      mobileMenuIsOpen: false,
      secondLevelPortal: null, // gesundheit, pflege, hilfsmittel
    };
  },
  methods: {
    // Check if the screen size is mobile
    onResize() {
      this.isMobile = window.innerWidth < MOBILE_HEADER_BREAKPOINT;
    },

    // Observe the height of the header
    observeHeaderHeight() {
      this.onChangeHeaderHeight(); // Call initially to set height
      // const debouncedFn = debounce(this.onChangeHeaderHeight, 100);
      this.headerResizeObserver = new ResizeObserver(this.onChangeHeaderHeight);
      this.headerResizeObserver.observe(this.$refs.navbarEl);
    },
    stopHeaderHeightObserver() {
      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
      }
    },

    // Update the height of the astro wrapper
    onChangeHeaderHeight() {
      const astroWrapper = document.querySelector(".anni-header-astro-wrapper");
      if (!astroWrapper) return;
      if (!this.$refs.navbarEl) return;

      const navbarHeight = this.$refs.navbarEl.getBoundingClientRect().height;
      const wrapperMinHeight = +astroWrapper.style.minHeight.replace("px", "");

      if (wrapperMinHeight === navbarHeight) return;
      astroWrapper.style.minHeight = `${navbarHeight}px`;
    },

    // Desktop menu controls
    expandDesktopMenu() {
      this.desktopMenuIsOpen = true;
    },
    collapseDesktopMenu() {
      this.desktopMenuIsOpen = false;
    },
    toggleDesktopMenu() {
      this.desktopMenuIsOpen = !this.desktopMenuIsOpen;
    },

    // Mobile menu controls
    toggleMobileMenu() {
      this.mobileMenuIsOpen = !this.mobileMenuIsOpen;
      document.body.style.overflow = this.mobileMenuIsOpen ? "hidden" : "";
      if (!this.mobileMenuIsOpen) this.onCloseMobileMenu();
    },
    onCloseMobileMenu() {
      const prescriptionDialog = document.querySelector(
        ".upload-prescription-dialog"
      );
      const prescriptionDialogIsOpen = !!prescriptionDialog;

      if (!prescriptionDialogIsOpen) {
        document.body.style.overflow = "unset";
        document.documentElement.style.overflow = "unset";
      }

      this.mobileMenuIsOpen = false;
      this.secondLevelPortal = null;
    },
    backToFirstLevel() {
      this.secondLevelPortal = null;
    },
    goToSecondLevel(portal) {
      this.secondLevelPortal = portal;
    },
  },
  mounted() {
    this.observeHeaderHeight();

    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });

    if (isPatientRoute()) {
      getHeaderData(this.storyblokVersion).then((res) => {
        this.overviewPagesData = res;
      });
    }

    // Remove the skeleton loader
    const skeletonLoader = document.querySelector(".anni-header-skeleton");
    if (skeletonLoader) skeletonLoader.remove();
  },
  beforeDestroy() {
    document.body.style.overflow = "";
    window.removeEventListener("resize", this.onResize);
    this.stopHeaderHeightObserver();
  },
  computed: {
    isInSecondLevel() {
      return !!this.secondLevelPortal;
    },
    headerTitle() {
      if (!this.isInSecondLevel) return "Menü";
      return this.secondLevelPortal.name;
    },
  },
  watch: {
    isMobile(mobile) {
      // Close the menu if the screen size changes to desktop
      if (!mobile) {
        if (this.mobileMenuIsOpen) {
          document.body.style.overflow = "";
          this.mobileMenuIsOpen = false;
          this.secondLevelPortal = null;
        }
      }
    },
    //
    // desktopMenuIsOpen(isOpen) {
    //   if (!this.$refs.navbarEl) return;
    // 	if (isOpen) return;

    //   const astroWrapper = document.querySelector(".anni-header-astro-wrapper");
    //   this.$refs.navbarEl.style.position = "sticky";
    //   if (astroWrapper) astroWrapper.style.position = "sticky";
    // },
  },
};
</script>

<template>
  <nav
    class="anni-navbar anni-header-v2"
    :class="{ 'menu-open': mobileMenuIsOpen, 'anni-mobile-header': isMobile }"
    ref="navbarEl"
  >
    <div class="navbar-container">
      <!-- Mobile menu toggler -->
      <MobileMenuToggler
        :mobileMenuIsOpen="mobileMenuIsOpen"
        :isInSecondLevel="isInSecondLevel"
        @toggleMobileMenu="toggleMobileMenu"
        @backToFirstLevel="backToFirstLevel"
      />

      <!-- Logo or Menu text -->
      <div class="logo-container">
        <a href="/" v-if="!mobileMenuIsOpen || !isMobile">
          <Logo />
        </a>
        <span v-else class="menu-label">{{ headerTitle }}</span>
      </div>

      <!-- Desktop navigation links/menu -->
      <DesktopMenu
        :portalMenuIsOpen="desktopMenuIsOpen"
        :overviewPagesData="overviewPagesData"
        @expandMenu="expandDesktopMenu"
        @collapseMenu="collapseDesktopMenu"
        @toggleMenu="toggleDesktopMenu"
      />
    </div>

    <!-- Provider subheader (Anbieter Finder checkout) -->
    <ProviderSubHeader />

    <!-- Mobile menu content -->
    <transition name="fade">
      <MobileMenuContent
        v-if="mobileMenuIsOpen"
        :isInSecondLevel="isInSecondLevel"
        :overviewPagesData="overviewPagesData"
        :secondLevelPortal="secondLevelPortal"
        @goToSecondLevel="goToSecondLevel"
      />
    </transition>

    <!-- Desktop expanded content (Informationen Portal) -->
    <transition name="big-menu-fade">
      <ExpandedContent
        v-if="desktopMenuIsOpen"
        :overviewPagesData="overviewPagesData"
        @collapseMenu="collapseDesktopMenu"
      />
    </transition>
  </nav>
</template>

<style lang="scss">
.anni-navbar {
  position: sticky;
  z-index: 1000;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #ffffff;
  box-shadow: 0 0 20px 7px #00000012;

  & * {
    font-family: Poppins, sans-serif;
  }

  & *:not(input):focus-visible {
    outline: 2px solid #35887e !important;
    outline-offset: 2px !important;
  }

  a {
    text-decoration: none;
    border-radius: 4px;
  }

  &.menu-open {
    // height: 100vh;
    overflow: hidden;
    background-color: #ffffff;
    // background-color: transparent;
  }

  .navbar-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 14px 16px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .logo-container {
    display: flex;
    align-items: center;
    margin-right: 2rem;

    .logo {
      height: 32px;
      width: auto;
    }

    .menu-label {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      color: #000;
    }
  }

  // Transitions
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .big-menu-fade-enter-active,
  .big-menu-fade-leave-active {
    transition: all 0.25s;
  }

  .big-menu-fade-enter-from,
  .big-menu-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  @media (max-width: 960px) {
    .logo-container {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }
  }
}
</style>
